import { SxProps, Theme } from '@mui/material';
import { brandColors, coarsePointerOnly } from '../../theme/mixins';

/**
 * The default width for a picker field.
 *
 * The native inputs these replace were capped narrow on purpose -- a time input is five
 * characters wide whatever the container is, and stretching it across a desktop card makes
 * it read as a text field somebody forgot to fill in. A picker is wider than that input,
 * because it carries an adornment button, so the old caps were re-measured rather than
 * carried over: 160px no longer fits `08:00` plus a 44px button without crowding.
 */
export const pickerFieldStyles: SxProps<Theme> = {
    width: { xs: '100%', sm: 200 },
};

/** A date reads wider than a clock time, and `18.08.2026` is ten characters. */
export const pickerDateFieldStyles: SxProps<Theme> = {
    width: { xs: '100%', sm: 220 },
};

/** A date and a time together, which is the widest of the three. */
export const pickerDateTimeFieldStyles: SxProps<Theme> = {
    width: { xs: '100%', sm: 260 },
};

/**
 * Room at the end of the field for the button that opens the picker.
 *
 * MUI caps an unshrunk outlined label at `calc(100% - 24px)`, and that 24px assumes a field
 * with nothing at its end. Every picker has something at its end -- `PickerFieldUI` always
 * renders the open-picker button as an end adornment -- and the theme grows that button to a
 * 44px target on a coarse pointer. So a long label is allowed to run under it and, because
 * the outlined label carries `zIndex: 1`, paints *over* the icon rather than behind it. The
 * calendar vanishing under `Valabil pana la` on a phone is exactly this.
 *
 * 76px is the 14px right padding plus the 8px adornment gap plus the 44px target, with a
 * little air. A label shorter than that is untouched, so this only ever ellipsizes one that
 * was about to collide anyway.
 *
 * Applied through the field's `inputLabel` slot rather than through its `sx`, which belongs
 * to the caller: two `sx` merged into an array is the one shape `SxProps` will not typecheck.
 */
export const pickerLabelClearanceStyles: SxProps<Theme> = {
    '&:not(.MuiInputLabel-shrink)': {
        maxWidth: 'calc(100% - 76px)',
    },
};

/**
 * The time combobox's list.
 *
 * Capped so it opens as a list rather than as a full-height wall of times -- a quarter-hour
 * step is 96 of them -- and scrolled to the selected one by Autocomplete itself. The rows
 * keep a finger-sized height on a touch screen, which is the whole reason the old spinner
 * wheel was tolerable on a phone and the two-column clock was not.
 */
export const timeListStyles: SxProps<Theme> = {
    maxHeight: 280,
    '& .MuiAutocomplete-option': {
        ...coarsePointerOnly({ minHeight: 44 }),
    },
};

/**
 * The calendar popup.
 *
 * Only the date pickers open one now -- the time picker is a combobox and dresses its own
 * list through `timeListStyles` above.
 *
 * MUI's own paper already picks up the theme's radius; what it does not do is take the
 * clinic's colours, because the theme is built from literal hex and the popup is drawn
 * outside any `*.styles.ts` that could reach `brandColors`. Applied through
 * `slotProps.desktopPaper` / `.mobilePaper` at the call site.
 */
export const pickerPopupStyles: SxProps<Theme> = {
    borderRadius: 3,
    // The selected day, the selected hour and the "today" ring are all brand-coloured, so
    // a clinic that chose green never sees MUI's default blue.
    '& .MuiPickersDay-root.Mui-selected': {
        backgroundColor: brandColors.primary,
        color: brandColors.primaryContrast,
    },
    '& .MuiPickersDay-today': {
        borderColor: brandColors.secondary,
    },
    '& .MuiPickersDay-root:not(.Mui-selected):not(.Mui-disabled)': {
        ...coarsePointerOnly({ minHeight: 40, minWidth: 40 }),
    },
    '& .MuiPickersCalendarHeader-label': {
        fontWeight: 600,
    },
    '& .MuiPickersYear-yearButton.Mui-selected': {
        backgroundColor: brandColors.primary,
        color: brandColors.primaryContrast,
    },
};

/**
 * The date-and-time pair.
 *
 * Wraps rather than scrolls: on a narrow dialog the time drops under the date, which is the
 * order they are answered in anyway. `min(Npx, 100%)` on the two children rather than a bare
 * pixel basis, so neither can push the dialog sideways.
 */
export const dateTimeRowStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    // 16px, which is the `mt: 2` every other field in a dialog is spaced by -- so the pair
    // keeps the page's rhythm whether it sits on one line or wraps onto two.
    gap: 2,
    alignItems: 'flex-start',
};

export const dateTimeDateStyles: SxProps<Theme> = {
    flex: '1 1 min(180px, 100%)',
    minWidth: 0,
};

export const dateTimeTimeStyles: SxProps<Theme> = {
    flex: '1 1 min(140px, 100%)',
    minWidth: 0,
};
